/**
 * Derives the minimal parent J-code inputs consumed by the sibling-order
 * filtering orchestrator from the live CPT table rows.
 * @module buildFilterJCodesFromTableData
 */
import moment from "moment";
import { FilterJCodeInput } from "../../../../../context/utils/filterSiblingOrdersBasedOnConditions";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { getAuthStatusIdFromText } from "../../../../../enums/medicalPaOrdersAuthStatus";

/**
 * Formats a raw table date-cell value into the `MM/DD/YYYY` string used across
 * the CPT payload derivations, keeping the sibling-filter inputs consistent
 * with {@link transformTableDataToApiPayload}.
 *
 * @param value - The raw date-cell value (any `Date`-parseable string)
 * @returns The formatted `MM/DD/YYYY` date, or an empty string when absent
 */
const toFormattedDate = (value?: string): string =>
  value ? moment(new Date(value).toISOString()).format("MM/DD/YYYY") : "";

/**
 * Maps each CPT table row to a {@link FilterJCodeInput}, carrying only the
 * fields the sibling-order filtering pipeline consumes. Mirrors the field
 * derivation in {@link transformTableDataToApiPayload} so the filtered result
 * reflects the user's in-progress edits rather than the persisted auth-on-file.
 *
 * @param tableData - Current CPT table model rows
 * @param authStatusOptions - Badge label → status id resolution list, used to
 *   resolve each row's `auth_status`
 * @returns The parent J-codes limited to the pipeline-relevant fields
 */
export const buildFilterJCodesFromTableData = (
  tableData: CptCodesTableRowModel[],
  authStatusOptions: AuthStatusOptionModel[],
): FilterJCodeInput[] =>
  tableData.map((row) => ({
    j_code: row.jCodes?.data || "",
    auth_status:
      getAuthStatusIdFromText(row.paRequired?.text ?? "", authStatusOptions) ||
      "",
    start_date: toFormattedDate(row.startFrom?.data),
    end_date: toFormattedDate(row.endAt?.data),
    visits_remaining: parseInt(row.remainingVisits?.data ?? "0", 10) || 0,
  }));
